import { type FC, type ReactNode, useId, useRef, useState } from "react";
import { Callout } from "ancestry-ui-react";
import { isEqual, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import { ExactnessButton } from "./ExactnessButton";
import styles from "./ExactnessSingleSelect.module.scss";

interface ExactnessOption {
    id: string;
    label: string;
    value: string;
}

interface ExactnessSingleSelectProps {
    value: string | null;
    options: ExactnessOption[];
    onChange: (value: string | null) => void;
    label: string;
    testId: string;
    footer?: ReactNode;
}

export const ExactnessSingleSelect: FC<ExactnessSingleSelectProps> = ({
    value,
    options,
    onChange,
    label,
    testId,
    footer,
}) => {
    invariant(options.length >= 2, "At least 2 options are required");
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const exactnessCheckboxId = useId();

    const isExact = !isNil(value);

    const getOptionId = (id: string) => `${exactnessCheckboxId}_option_${id}`;

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsOpen(false);
            triggerRef.current?.focus();
        }
    };

    return (
        <>
            <ExactnessButton
                ref={triggerRef}
                isExact={isExact}
                onClick={() => {
                    if (isExact) return;
                    invariant(options[0], "option[0] not found");
                    onChange(options[0].value);
                }}
                testId={`${testId}-trigger`}
            >
                {label}
            </ExactnessButton>
            <div className="calloutContainer">
                <Callout
                    trigger={triggerRef}
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <div data-testid={`${testId}-content`} onKeyDown={handleKeyDown}>
                        <div>
                            <input
                                id={exactnessCheckboxId}
                                className="checkbox"
                                type="checkbox"
                                onChange={() => {
                                    invariant(options[0], "option[0] not found");
                                    onChange(isNil(value) ? options[0].value : null);
                                }}
                                checked={isExact}
                                data-testid={`${testId}-exact-checkbox`}
                            />
                            <label htmlFor={exactnessCheckboxId}>{t("exactTo")}</label>
                        </div>
                        {options.map((option) => (
                            <div key={option.id} className={styles.optionPadding}>
                                <input
                                    className="radio"
                                    type="radio"
                                    id={getOptionId(option.id)}
                                    value={option.value}
                                    checked={isEqual(value, option.value)}
                                    onChange={() => onChange(option.value)}
                                    data-testid={`${testId}-option-${option.id}`}
                                />
                                <label htmlFor={getOptionId(option.id)}>{option.label}</label>
                            </div>
                        ))}
                        {footer}
                    </div>
                </Callout>
            </div>
        </>
    );
};
